import { AsYouType } from "libphonenumber-js/max";
import type { CountryCode } from "./regions";

export interface Edit {
  value: string;
  caret: number;
  // What the box held before the keystroke, which is the only way to tell a deletion from anything else.
  previous: string;
}

export interface Typed {
  value: string;
  caret: number;
}

// What the box should hold after a keystroke, and where the caret should sit in it. The separators are the page's
// own and are written afresh every time, so what is carried across an edit is the characters somebody actually typed
// and their count either side of the caret — putting the caret back by its old offset would walk it every time a
// space appeared in front of it.
export function retype(edit: Edit, region: CountryCode): Typed {
  if (!PLAIN.test(edit.value)) return unformatted(edit);

  const typed = significant(edit.value);
  const before = significant(edit.value.slice(0, edit.caret)).length;

  // A backspace that landed on a space or a bracket took a character this page put there rather than one anybody
  // typed, and reformatting would put it straight back — so the digit in front of it goes instead, and the key does
  // what it looked like it was going to do. Without this, backspace at the end of "+61 " is a key that does nothing.
  const separator = edit.value.length < edit.previous.length && typed === significant(edit.previous);
  const at = separator ? Math.max(before - 1, 0) : before;
  const kept = separator ? typed.slice(0, at) + typed.slice(before) : typed;

  const value = format(kept, region);
  return { value, caret: caretAfter(value, at) };
}

// The same formatting for a box nobody is typing into: the picker has just changed which country the digits are read
// as, and 0412345678 is spaced one way in Australia and not at all in New Zealand.
export function retypeAll(value: string, region: CountryCode): string {
  return PLAIN.test(value) ? format(significant(value), region) : value;
}

// What happens the moment a letter arrives: formatting stops, and the separators it had already put in come back out
// with it. Typing 1-800-FLOWERS otherwise spells "1 (800" before the F is reached and leaves the bracket stranded
// among the letters, which is the page having mangled a number nobody could have typed that way.
function unformatted(edit: Edit): Typed {
  // One character is somebody typing, and every separator in front of it is this page's own, the value before the
  // keystroke having been its own output. More than one is a paste, whose separators came with it and are theirs.
  const typing = PLAIN.test(edit.previous) && edit.value.length === edit.previous.length + 1;
  if (!typing) return { value: edit.value, caret: edit.caret };

  const dropped = [...edit.value.slice(0, edit.caret)].filter((character) => !kept(character)).length;
  return { value: [...edit.value].filter(kept).join(""), caret: edit.caret - dropped };
}

function kept(character: string): boolean {
  return SIGNIFICANT.test(character) || !SEPARATOR.test(character);
}

// Only a value that is plainly digits and separators is respaced. A letter means an extension somebody wrote out in
// words or a number spelled in them — 1-800-FLOWERS — and cutting either back to digits would be the page destroying
// what it was handed rather than tidying it; the same goes for the `;ext=` and `#` an extension arrives after.
const PLAIN = /^[+\d\s().\-/]*$/;

// Everything the formatter puts between the digits, which is everything a plain value holds that nobody typed.
const SEPARATOR = /[\s().\-/]/;

// The leading plus and the digits, which are the whole of what anybody typed.
function significant(text: string): string {
  const digits = text.replace(/\D/g, "");
  return text.trimStart().startsWith("+") ? `+${digits}` : digits;
}

// A formatter follows a number as it grows and never as it shrinks, so a fresh one is handed the whole of what was
// typed: an edit in the middle is a different number from the one the last formatter had been following.
function format(typed: string, region: CountryCode): string {
  return new AsYouType(region).input(typed);
}

// Just past the nth character somebody typed, wherever the formatting has since put it.
function caretAfter(value: string, count: number): number {
  if (count <= 0) return 0;
  let seen = 0;
  for (let at = 0; at < value.length; at++) {
    if (SIGNIFICANT.test(value[at]) && ++seen === count) return at + 1;
  }
  return value.length;
}

const SIGNIFICANT = /[+\d]/;
